import PropTypes from "prop-types"

// Allowed keys for navigation and editing
const ALLOWED_KEYS = [
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  ".",
  ",",
]

export const NumberInput = ({
  id,
  name,
  value = "",
  min = 0,
  max = Infinity,
  step,
  onChange,
  onBlur,
  onKeyDown,
  className = "text-input",
  placeholder,
  disabled = false,
  required = false,
  ...props
}) => {
  const minimum = min
  const maximum = max

  const clampValue = (value) => {
    if (value === "" || value === "-") return value
    const numValue = parseFloat(value)
    if (isNaN(numValue)) return minimum.toString()
    return Math.max(minimum, Math.min(maximum, numValue)).toString()
  }

  const handleChange = (e) => {
    const inputValue = e.target.value

    // Allow empty string or minus sign
    if (inputValue === "" || inputValue === "-") {
      onChange(e)
      return
    }

    onChange(e)
  }

  const handleBlur = (e) => {
    const clampedValue = e.target.value !== "" ? clampValue(e.target.value) : ""

    // Create a new event with the clamped value
    const newEvent = {
      ...e,
      target: { ...e.target, value: clampedValue },
    }

    onChange(newEvent)

    if (onBlur) {
      onBlur(newEvent)
    }
  }

  const handleKeyDown = (e) => {
    // Allow navigation and editing keys
    if (ALLOWED_KEYS.includes(e.key)) {
      if (onKeyDown) onKeyDown(e)
      return
    }

    // Allow any key when modifiers are used
    if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
      if (onKeyDown) onKeyDown(e)
      return
    }

    // Allow minus sign only at the beginning
    if (e.key === "-" && e.target.value === "") {
      if (onKeyDown) onKeyDown(e)
      return
    }

    // Block everything else that's not a number
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault()
    }

    if (onKeyDown) onKeyDown(e)
  }

  return (
    <input
      type="number"
      inputMode="decimal"
      id={id}
      name={name}
      min={minimum}
      max={maximum}
      step={step}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      {...props}
    />
  )
}

NumberInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
}

export default NumberInput
